import { Capacitor } from "@capacitor/core"
import { Translation, Language } from "@capacitor-mlkit/translation"
import { SupportedLanguage } from "./languageStorage"
import { getCachedTranslation, setCachedTranslation } from "./translationCache"
import { getOverride } from "./translationOverrides"

const languageMap: Record<SupportedLanguage, Language> = {
    ja: Language.Japanese,
    en: Language.English
}

export const isTranslationAvailable = (): boolean =>
    Capacitor.isNativePlatform()

export const downloadLanguageModel = async (
    language: SupportedLanguage
): Promise<void> => {
    if (!isTranslationAvailable()) return
    await Translation.downloadModel({ language: languageMap[language] })
}

export const isModelDownloaded = async (
    language: SupportedLanguage
): Promise<boolean> => {
    if (!isTranslationAvailable()) return false
    const { languages } = await Translation.getDownloadedModels()
    return languages.includes(languageMap[language])
}

export const getDownloadedLanguages = async (): Promise<
    SupportedLanguage[]
> => {
    if (!isTranslationAvailable()) return ["ja"]
    const { languages } = await Translation.getDownloadedModels()
    const reverseMap = Object.entries(languageMap) as [
        SupportedLanguage,
        Language
    ][]
    const downloaded = reverseMap
        .filter(([, mlLang]) => languages.includes(mlLang))
        .map(([lang]) => lang)
    return ["ja", ...downloaded.filter(l => l !== "ja")]
}

export const deleteLanguageModel = async (
    language: SupportedLanguage
): Promise<void> => {
    if (!isTranslationAvailable()) return
    await Translation.deleteDownloadedModel({
        language: languageMap[language]
    })
}

export const translateText = async (
    text: string,
    targetLanguage: SupportedLanguage
): Promise<string> => {
    if (targetLanguage === "ja") return text
    if (!isTranslationAvailable()) return text

    const cached = getCachedTranslation(targetLanguage, text)
    if (cached !== undefined) return cached

    const override = getOverride(targetLanguage, text)
    if (override !== undefined) {
        setCachedTranslation(targetLanguage, text, override)
        return override
    }

    try {
        const result = await Translation.translate({
            text,
            sourceLanguage: Language.Japanese,
            targetLanguage: languageMap[targetLanguage]
        })
        setCachedTranslation(targetLanguage, text, result.text)
        return result.text
    } catch {
        return text
    }
}
